import { isValidPostalCode, validateHttpsUrl } from '@Utils';
import { CUSTOM_LINK_FIELDS, SOCIAL_MEDIA_TYPES } from '../../../constants';
import {
  FEATURES,
  MARKETING_MEDIA,
  MARKETING_MEDIA_FIELDS,
  NOTES_FEATURES_FIELDS,
  OVERVIEW,
  PROPERTY_DETAILS,
  PROPERTY_DETAILS_FIELDS,
  VALIDATION_ERROR_MESSAGES,
} from './constants';

const propertyDetailFieldList = Object.values(PROPERTY_DETAILS_FIELDS);

export const digitInputFilter = (value) => value.replace(/\D/g, '');

export const formatYearBuilt = (value) => {
  const year = Number(digitInputFilter(value).slice(0, 4));
  return year ? year.toString() : '';
};

export const formatFieldValue = (field, value) => {
  switch (field) {
    case PROPERTY_DETAILS_FIELDS.TOTAL_UNITS:
      return Number(digitInputFilter(value));

    case PROPERTY_DETAILS_FIELDS.YEAR_BUILT:
      return formatYearBuilt(value);

    default:
      return value;
  }
};

export const stringArraysDiffer = (valuesA, valuesB) => {
  const listA = valuesA ?? [];
  const listB = valuesB ?? [];

  if (listA.length !== listB.length) {
    return true;
  }

  const uniqueCombined = new Set([...listA, ...listB]);
  return listA.length !== uniqueCombined.size;
};

export const hasUnitTypesChanged = (originalUnitTypes = [], unitTypes = []) =>
  stringArraysDiffer(originalUnitTypes, unitTypes);

export const hasImagesChanged = (originalImages, updatedImages) => {
  if (originalImages.length !== updatedImages.length) {
    return true;
  }

  const updatedImagesMap = updatedImages.reduce((imageMap, image) => {
    imageMap[image.url] = image;
    return imageMap;
  }, {});

  return originalImages.some((originalImage) => {
    const updatedImage = updatedImagesMap[originalImage.url];
    if (!updatedImage) {
      return true;
    }

    const updatedDescription = updatedImage.description || '';
    const originalDescription = originalImage.description || '';

    return originalDescription !== updatedDescription;
  });
};

export const hasPhotosChanged = (originalValues, values) => {
  const originalPhotos = originalValues[MARKETING_MEDIA_FIELDS.PHOTOS] || [];
  const updatedPhotos = values[MARKETING_MEDIA_FIELDS.PHOTOS] || [];

  return hasImagesChanged(originalPhotos, updatedPhotos);
};

export const hasLogosChanged = (originalValues, values) => {
  const originalLogos = originalValues[MARKETING_MEDIA_FIELDS.LOGOS] || [];
  const updatedLogos = values[MARKETING_MEDIA_FIELDS.LOGOS] || [];

  return hasImagesChanged(originalLogos, updatedLogos);
};

export const hasBrandColorChanged = (originalValues, values) => {
  return (
    originalValues[MARKETING_MEDIA_FIELDS.BRAND_COLOR] !==
    values[MARKETING_MEDIA_FIELDS.BRAND_COLOR]
  );
};

export const hasCustomLinksChanged = (originalValues, values) => {
  const originalCustomLinks =
    originalValues[MARKETING_MEDIA_FIELDS.CUSTOM_LINKS] || [];

  const updatedCustomLinks = values[MARKETING_MEDIA_FIELDS.CUSTOM_LINKS] || [];

  if (originalCustomLinks.length !== updatedCustomLinks.length) {
    return true;
  }

  const updatedCustomLinksMap = updatedCustomLinks.reduce((linkMap, link) => {
    linkMap[link[CUSTOM_LINK_FIELDS.URL]] = link;
    return linkMap;
  }, {});

  return originalCustomLinks.some((originalLink) => {
    const updatedLink =
      updatedCustomLinksMap[originalLink[CUSTOM_LINK_FIELDS.URL]];
    if (!updatedLink) {
      return true;
    }

    const { [CUSTOM_LINK_FIELDS.TITLE]: originalTitle } = originalLink;
    const { [CUSTOM_LINK_FIELDS.TITLE]: updatedTitle } = updatedLink;

    return originalTitle !== updatedTitle;
  });
};

export const hasPropertyWebsiteChanged = (originalValues, values) => {
  const originalWebsite =
    originalValues[MARKETING_MEDIA_FIELDS.PROPERTY_WEBSITE] || '';
  const updatedWebsite = values[MARKETING_MEDIA_FIELDS.PROPERTY_WEBSITE] || '';
  return originalWebsite !== updatedWebsite;
};

export const hasSocialMediaChanged = (originalValues, values) => {
  const originalSocialMedia =
    originalValues[MARKETING_MEDIA_FIELDS.SOCIAL_MEDIA] || {};
  const updatedSocialMedia = values[MARKETING_MEDIA_FIELDS.SOCIAL_MEDIA] || {};

  const socialMediaTypes = Object.values(SOCIAL_MEDIA_TYPES);
  return socialMediaTypes.some((socialMediaType) => {
    const originalUrl = originalSocialMedia[socialMediaType] || '';
    const updatedUrl = updatedSocialMedia[socialMediaType] || '';
    return originalUrl !== updatedUrl;
  });
};

export const hasMarketingMediaChanged = (originalValues = {}, values = {}) => {
  return (
    hasBrandColorChanged(originalValues, values) ||
    hasLogosChanged(originalValues, values) ||
    hasPhotosChanged(originalValues, values) ||
    hasPropertyWebsiteChanged(originalValues, values) ||
    hasSocialMediaChanged(originalValues, values) ||
    hasCustomLinksChanged(originalValues, values)
  );
};

export const hasPropertyDetailsChanged = (originalValues = {}, values = {}) => {
  return propertyDetailFieldList.some((field) => {
    if (field === PROPERTY_DETAILS_FIELDS.UNIT_TYPES) {
      return hasUnitTypesChanged(originalValues[field], values[field]);
    }
    return originalValues[field] !== values[field];
  });
};

const notesFeaturesFieldNames = Object.values(NOTES_FEATURES_FIELDS);

export const hasNotesFeaturesChanged = (originalValues = {}, values = {}) => {
  return notesFeaturesFieldNames.some((fieldName) => {
    const orig = originalValues[fieldName];
    const next = values[fieldName];

    if (fieldName === NOTES_FEATURES_FIELDS.NOTES) {
      return orig !== next;
    }

    return stringArraysDiffer(orig || [], next || []);
  });
};

export const getHasOverviewChanged = (overview, overviewTabData) => {
  return (
    hasPropertyDetailsChanged(
      overview[PROPERTY_DETAILS],
      overviewTabData[PROPERTY_DETAILS],
    ) ||
    hasMarketingMediaChanged(
      overview[MARKETING_MEDIA],
      overviewTabData[MARKETING_MEDIA],
    ) ||
    hasNotesFeaturesChanged(
      overview[FEATURES] ?? {},
      overviewTabData[FEATURES] ?? {},
    )
  );
};

export const getLogosChanges = (originalValues, values) => {
  if (hasLogosChanged(originalValues, values)) {
    return {
      [MARKETING_MEDIA_FIELDS.LOGOS]:
        values[MARKETING_MEDIA_FIELDS.LOGOS] || [],
    };
  }
  return {};
};

export const getPhotosChanges = (originalValues, values) => {
  if (hasPhotosChanged(originalValues, values)) {
    return {
      [MARKETING_MEDIA_FIELDS.PHOTOS]:
        values[MARKETING_MEDIA_FIELDS.PHOTOS] || [],
    };
  }
  return {};
};

export const getBrandColorChanges = (originalValues, values) =>
  hasBrandColorChanged(originalValues, values)
    ? {
        [MARKETING_MEDIA_FIELDS.BRAND_COLOR]:
          values[MARKETING_MEDIA_FIELDS.BRAND_COLOR],
      }
    : {};

export const getMarketingMediaChanges = (originalValues = {}, values = {}) => {
  const brandColorChanges = getBrandColorChanges(originalValues, values);

  const logosChanges = getLogosChanges(originalValues, values);
  const photosChanges = getPhotosChanges(originalValues, values);

  const marketingMediaChanges = {
    ...brandColorChanges,
    ...logosChanges,
    ...photosChanges,
  };

  return Object.keys(marketingMediaChanges).length
    ? { [MARKETING_MEDIA]: marketingMediaChanges }
    : {};
};

export const getPropertyDetailChanges = (originalValues, values) => {
  const fieldChanges = propertyDetailFieldList.reduce((updates, field) => {
    const value = values[field];
    const originalValue = originalValues[field];

    const hasChanged =
      field === PROPERTY_DETAILS_FIELDS.UNIT_TYPES
        ? hasUnitTypesChanged(originalValue, value)
        : originalValue !== value;
    if (hasChanged) {
      updates[field] = value;
    }
    return updates;
  }, {});

  const updates = {
    ...fieldChanges,
  };

  return Object.keys(updates).length ? { [PROPERTY_DETAILS]: updates } : {};
};

export const getNotesFeaturesChanges = (originalValues = {}, values = {}) => {
  const updates = notesFeaturesFieldNames.reduce((fieldChanges, fieldName) => {
    const orig = originalValues[fieldName];
    const next = values[fieldName];

    const isChanged =
      fieldName === NOTES_FEATURES_FIELDS.NOTES
        ? orig !== next
        : stringArraysDiffer(orig || [], next || []);

    if (isChanged) {
      fieldChanges[fieldName] = next;
    }
    return fieldChanges;
  }, {});

  return Object.keys(updates).length ? { [FEATURES]: updates } : {};
};

export const getOverviewChanges = (overview, overviewTabData) => {
  const propertyDetailsUpdates = getPropertyDetailChanges(
    overview[PROPERTY_DETAILS],
    overviewTabData[PROPERTY_DETAILS],
  );

  const marketingMediaUpdates = getMarketingMediaChanges(
    overview[MARKETING_MEDIA],
    overviewTabData[MARKETING_MEDIA],
  );

  const notesFeaturesUpdates = getNotesFeaturesChanges(
    overview[FEATURES],
    overviewTabData[FEATURES],
  );

  return {
    [OVERVIEW]: {
      ...propertyDetailsUpdates,
      ...marketingMediaUpdates,
      ...notesFeaturesUpdates,
    },
  };
};

export const getValidationErrorMessage = (field, value) => {
  if (field === PROPERTY_DETAILS_FIELDS.YEAR_BUILT) {
    return value && value.length !== 4
      ? VALIDATION_ERROR_MESSAGES.INVALID_YEAR_BUILT
      : '';
  }

  if (!value) {
    return VALIDATION_ERROR_MESSAGES.REQUIRED;
  }

  if (field === PROPERTY_DETAILS_FIELDS.ZIP) {
    return isValidPostalCode(value)
      ? ''
      : VALIDATION_ERROR_MESSAGES.INVALID_ZIP;
  }
};

export const getPropertyDetailErrorMessages = (propertyDetails) => {
  const fields = [
    PROPERTY_DETAILS_FIELDS.CITY,
    PROPERTY_DETAILS_FIELDS.PROPERTY_NAME,
    PROPERTY_DETAILS_FIELDS.STATE,
    PROPERTY_DETAILS_FIELDS.STREET,
    PROPERTY_DETAILS_FIELDS.YEAR_BUILT,
    PROPERTY_DETAILS_FIELDS.ZIP,
  ];

  const validationErrorMessages = fields.reduce((errorMessages, field) => {
    const value = propertyDetails[field];
    const errorMessage = getValidationErrorMessage(field, value);

    if (errorMessage) {
      errorMessages[field] = errorMessage;
    }

    return errorMessages;
  }, {});

  return validationErrorMessages;
};

export const getUrlErrorMessage = (url) => {
  if (!url) {
    return;
  }

  const isValidUrl = validateHttpsUrl(url);
  if (!isValidUrl) {
    return VALIDATION_ERROR_MESSAGES.INVALID_URL;
  }
};

export const getMarketingMediaErrorMessages = (marketingMedia) => {
  const urlFields = [
    SOCIAL_MEDIA_TYPES.FACEBOOK,
    SOCIAL_MEDIA_TYPES.INSTAGRAM,
    SOCIAL_MEDIA_TYPES.X,
    SOCIAL_MEDIA_TYPES.YELP,
  ];

  const validationErrorMessages = urlFields.reduce((errorMessages, field) => {
    const { [MARKETING_MEDIA_FIELDS.SOCIAL_MEDIA]: socialMedia } =
      marketingMedia;
    const url = socialMedia[field];

    const errorMessage = getUrlErrorMessage(url);
    if (errorMessage) {
      errorMessages[field] = errorMessage;
    }

    return errorMessages;
  }, {});

  const propertyWebsiteUrl =
    marketingMedia[MARKETING_MEDIA_FIELDS.PROPERTY_WEBSITE];

  if (propertyWebsiteUrl) {
    const errorMessage = getUrlErrorMessage(propertyWebsiteUrl);
    if (errorMessage) {
      validationErrorMessages[MARKETING_MEDIA_FIELDS.PROPERTY_WEBSITE] =
        errorMessage;
    }
  }

  return validationErrorMessages;
};

export const getValidationErrorMessages = (values) => {
  const propertyDetailErrorMessages = getPropertyDetailErrorMessages(
    values[PROPERTY_DETAILS],
  );

  const marketingMediaErrorMessages = getMarketingMediaErrorMessages(
    values[MARKETING_MEDIA],
  );

  const errorMessages = {
    ...propertyDetailErrorMessages,
    ...marketingMediaErrorMessages,
  };

  return Object.keys(errorMessages).length ? errorMessages : null;
};
